import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GameType } from '../../types/GameType.ts';
import { getGameConfig } from '../../types/GameConfig.ts';
import { ArchiveGameStatus } from '../../utils/archiveUtils.ts';

interface ArchiveCalendarDayProps {
  day: number;
  gameNumber: number | null;
  status: ArchiveGameStatus | null;
  gameType: GameType;
  isToday: boolean;
}

// Star SVG for beaten state — replaces the day number
const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

function createLightColor(primaryColor: string): string {
  const hex = primaryColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const lightR = Math.round(r * 0.2 + 255 * 0.8);
  const lightG = Math.round(g * 0.2 + 255 * 0.8);
  const lightB = Math.round(b * 0.2 + 255 * 0.8);
  return `rgb(${lightR}, ${lightG}, ${lightB})`;
}

export default function ArchiveCalendarDay(props: ArchiveCalendarDayProps) {
  const { day, gameNumber, status, gameType, isToday } = props;
  const navigate = useNavigate();
  const gameConfig = getGameConfig(gameType);
  const primaryColor = gameConfig.primaryColor;

  // Empty cell (no game for this day)
  if (gameNumber == null || status == null) {
    return <div className="archive-calendar-day archive-calendar-day--empty" />;
  }

  const handleClick = () => {
    if (isToday) {
      // Today's date navigates to today's game, not the archive version
      navigate(gameConfig.routePath);
    } else {
      const basePath = gameConfig.routePath.endsWith('/')
        ? gameConfig.routePath.slice(0, -1)
        : gameConfig.routePath;
      navigate(`${basePath}/game/${gameNumber}`);
    }
  };

  let backgroundColor: string;
  let color: string;
  let isBeaten = false;

  switch (status) {
    case 'beaten':
      backgroundColor = primaryColor;
      color = '#fff';
      isBeaten = true;
      break;
    case 'in-progress':
      backgroundColor = createLightColor(primaryColor);
      color = '#333';
      break;
    case 'not-played':
    default:
      backgroundColor = '#e0e0e0';
      color = '#333';
      break;
  }

  return (
    <div
      className={`archive-calendar-day archive-calendar-day--clickable${isToday ? ' archive-calendar-day--today' : ''}`}
      style={{ backgroundColor }}
      onClick={handleClick}
    >
      {isBeaten ? (
        <StarIcon />
      ) : (
        <span className="archive-calendar-day-number" style={{ color }}>
          {day}
        </span>
      )}
    </div>
  );
}
